import { Actions, createEffect, ofType } from '@ngrx/effects'
import { SNACKBAR_LENGTH_SHORT, SnackbarService } from '@shared/utils/snackbar.service'
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { ChangeRequest, ChangeRequestStatus } from '@app/core/model/change-request.model'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Store } from '@ngrx/store'
import { ChangeRequestService } from '../../service/change-request.service'
import { ChangeRequestActions } from '../actions/change-request.actions'

@Injectable()
export class ChangeRequestFlowEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private changeRequestService: ChangeRequestService,
    private snackbarService: SnackbarService
  ) {}

  getChangeRequestAbleToMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.getChangeRequestSuccess),
      filter(
        ({ changeRequest }) =>
          changeRequest.status !== ChangeRequestStatus.MERGED_CLOSED &&
          changeRequest.status !== ChangeRequestStatus.UNMERGED_CLOSED
      ),
      map(({ changeRequest }) => ChangeRequestActions.changeRequestAbleToMerge({ changeRequestId: changeRequest.id }))
    )
  )

  changeRequestAbleToMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.changeRequestAbleToMerge),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId }, currentScope]) =>
        this.changeRequestService.changeRequestAbleToMerge(scopeId || currentScope.identity.id, changeRequestId).pipe(
          map((ableToMerge: boolean) => ChangeRequestActions.changeRequestAbleToMergeSuccess({ ableToMerge })),
          catchError((error) => of(ChangeRequestActions.changeRequestAbleToMergeFail({ error })))
        )
      )
    )
  )

  dismissChangeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.dismissChangeRequest),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId, comment }, currentScope]) =>
        this.changeRequestService
          .dismissChangeRequest(scopeId || currentScope.identity.id, changeRequestId, { comment })
          .pipe(
            map((changeRequest: ChangeRequest) => {
              this.snackbarService.showInfoSnackbar(
                `Change Request ${changeRequest?.name} dismissed successfully`,
                SNACKBAR_LENGTH_SHORT
              )
              return ChangeRequestActions.dismissChangeRequestSuccess({ changeRequest })
            }),
            catchError((error: unknown) => {
              this.snackbarService.showErrorSnackbar('An error occurred while dismissing change request')
              console.error(JSON.stringify(error))
              return of(ChangeRequestActions.dismissChangeRequestFail({ error: error as Error }))
            })
          )
      )
    )
  )

  revertChangeRequestToDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.revertChangeRequestToDraft),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([{ scopeId, changeRequestId, comment }, currentScope]) =>
        this.changeRequestService
          .revertChangeRequestToDraft(scopeId || currentScope.identity.id, changeRequestId, { comment })
          .pipe(
            map((changeRequest: ChangeRequest) => {
              this.snackbarService.showInfoSnackbar(
                `Change Request ${changeRequest?.name} reverted to draft successfully`,
                SNACKBAR_LENGTH_SHORT
              )
              return ChangeRequestActions.revertChangeRequestToDraftSuccess({ changeRequest })
            }),
            catchError((error: unknown) => {
              this.snackbarService.showErrorSnackbar('An error occurred while reverting change request to draft')
              console.error(JSON.stringify(error))
              return of(ChangeRequestActions.revertChangeRequestToDraftFail({ error: error as Error }))
            })
          )
      )
    )
  )
}
